
import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';
import { SwingOpportunity, SellSignal } from './useSwingTrader';

interface MobileSwingProps {
    data: any;
    activeTab: string;
    setActiveTab: (tab: any) => void;
    strategy: 'swing' | 'trend';
    setStrategy: (strat: 'swing' | 'trend') => void;
    opportunities: SwingOpportunity[];
    sellSignals: SellSignal[];
    eligibleUniverse: any[];
    sentiment: string;
    sentimentColors: any;
    navigate: (path: string) => void;
}

const MobileSwing: React.FC<MobileSwingProps> = ({
    data,
    activeTab,
    setActiveTab,
    strategy,
    setStrategy,
    opportunities,
    sellSignals,
    eligibleUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    return (
        <div style={{ padding: '16px', maxWidth: '100vw', overflowX: 'hidden' }}>
            <Disclaimer type="investment" />

            {/* Mobile Summary Grid */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>🔥 Opps</span>
                        <span style={styles.summaryValue}>{opportunities.length}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>⚠️ Alertes</span>
                        <span style={styles.summaryValue}>{sellSignals.length}</span>
                    </div>
                </div>
            </Box>

            {/* Scrollable Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Achats ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Ventes ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Aide
                </button>
            </div>

            {/* Opportunities (Cards) */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>
                                <p style={{ fontSize: '14px' }}>Aucune oppportunité détectée.</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div onClick={() => navigate(`/data?symbol=${opp.symbole}`)} style={{ cursor: 'pointer' }}>
                                            <span style={styles.symbolLink}>{opp.symbole}</span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={{ ...styles.scoreBadge, backgroundColor: opp.swing_score >= 80 ? '#16a34a' : '#d97706' }}>
                                            {opp.swing_score}
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Entrée</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString()}</span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Objectif</span>
                                            <span style={{ ...styles.priceValue, color: '#16a34a' }}>{opp.target_price.toLocaleString()}</span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Stop</span>
                                            <span style={{ ...styles.priceValue, color: '#dc2626' }}>{opp.stop_loss.toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div style={styles.infoRow}>
                                        <span>📦 x{opp.position_size}</span>
                                        <span>💰 {opp.position_value.toLocaleString()} XOF</span>
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '14px' }}>Aucune alerte de vente.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal: any) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div onClick={() => navigate(`/data?symbol=${signal.symbole}`)} style={{ cursor: 'pointer' }}>
                                            <span style={styles.symbolLink}>{signal.symbole}</span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <span style={{ ...styles.badge, backgroundColor: signal.signal_strength === 'STRONG' ? '#fee2e2' : '#fef3c7', color: signal.signal_strength === 'STRONG' ? '#dc2626' : '#d97706', fontSize: '10px' }}>
                                            {signal.signal_strength === 'STRONG' ? '⚠️ FORT' : '⚡ MOYEN'}
                                        </span>
                                    </div>

                                    <div style={styles.pnlGrid}>
                                        <div style={styles.pnlItem}>
                                            <span style={styles.priceLabel}>P&L</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#16a34a' : '#dc2626' }}>
                                                {signal.unrealized_pnl_pct?.toFixed(1)}%
                                            </span>
                                        </div>
                                        <div style={styles.pnlItem}>
                                            <span style={styles.priceLabel}>Valeur</span>
                                            <span style={styles.priceValue}>{signal.quantity_held} actions</span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{signal.justification}</p>

                                    <div style={styles.actionRow}>
                                        <span style={{ ...styles.actionBadge, backgroundColor: signal.recommended_action === 'SELL_ALL' ? '#dc2626' : '#f97316' }}>
                                            {signal.recommended_action === 'SELL_ALL' ? 'VENDRE TOUT' : 'ALLÉGER'}
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        )
                    ))}
                </div>
            )}

            {/* Universe (Cards on mobile) */}
            {activeTab === 'universe' && (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                        <button
                            style={{ ...styles.filterChip, ...(strategy === 'swing' ? styles.filterChipActive : {}) }}
                            onClick={() => setStrategy('swing')}
                        >
                            ⚡ Swing (Rebond)
                        </button>
                        <button
                            style={{ ...styles.filterChip, ...(strategy === 'trend' ? styles.filterChipActiveTrend : {}) }}
                            onClick={() => setStrategy('trend')}
                        >
                            🏄 Trend Surfer
                        </button>
                    </div>

                    <div style={styles.cardsGrid}>
                        {eligibleUniverse.slice(0, 20).map((stock: any) => {
                            const isOpportunity = strategy === 'swing' ? stock.is_swing_opportunity : stock.is_trend_opportunity;
                            const score = strategy === 'swing' ? stock.swing_score : stock.trend_surfer_score;

                            return (
                                <Box key={stock.symbole}>
                                    <div style={{ 
                                        ...styles.card, 
                                        borderLeft: isOpportunity ? (strategy === 'swing' ? '4px solid #288fb4' : '4px solid #7c3aed') : '1px solid var(--border-color)' 
                                    }} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{stock.symbole}</span>
                                                {isOpportunity && <span style={{ marginLeft: 6, fontSize: '10px' }}>🎯</span>}
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{stock.cours_actuel?.toLocaleString()}</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 15, marginTop: 8, fontSize: '12px', color: 'var(--text-muted)' }}>
                                            <span>Score: <b style={{ color: score > 70 ? '#16a34a' : 'inherit' }}>{score}</b></span>
                                            <span>RSI: {stock.technical.rsi_14?.toFixed(0)}</span>
                                            <span>Statut: {stock.is_owned ? '📍' : '—'}</span>
                                        </div>
                                    </div>
                                </Box>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 16 }}>
                        <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>📊 Guide Swing Trader</h3>
                        <div style={{ fontSize: '13px', lineHeight: '1.5', color: 'var(--text-main)' }}>
                            <p>Analyse technique court terme (3-10 jours).</p>
                            <h4 style={{ marginTop: 15, fontSize: '14px' }}>⚡ Stratégies</h4>
                            <ul>
                                <li><strong>Swing :</strong> Profiter d'un rebond technique sur support.</li>
                                <li><strong>Trend :</strong> Accompagner une tendance haussière forte.</li>
                            </ul>
                        </div>
                    </div>
                </Box>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px' },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: '11px', color: 'var(--text-muted)', marginBottom: 2 },
    summaryValue: { fontSize: '18px', fontWeight: 'bold', color: 'var(--text-heading)' },
    badge: { padding: '4px 10px', borderRadius: 20, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none' },
    tab: { padding: '8px 14px', border: 'none', borderRadius: 20, fontSize: '12px', fontWeight: '600', cursor: 'pointer', backgroundColor: '#f1f5f9', color: '#445', whiteSpace: 'nowrap' },
    tabActive: { backgroundColor: '#16a34a', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveInfo: { backgroundColor: '#2563eb', color: 'white' },
    cardsGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
    card: { padding: '14px', backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
    symbolLink: { fontSize: '16px', fontWeight: 'bold', color: '#2563eb' },
    companyName: { fontSize: '11px', color: 'var(--text-muted)', margin: '2px 0 0 0' },
    scoreBadge: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '13px' },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12, padding: '10px', backgroundColor: '#f8fafc', borderRadius: 8 },
    priceItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    priceLabel: { fontSize: '9px', color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' },
    priceValue: { fontSize: '12px', fontWeight: 'bold', color: 'var(--text-heading)' },
    infoRow: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', marginBottom: 10, padding: '6px 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' },
    justification: { fontSize: '12px', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic', lineHeight: '1.4' },
    pnlGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12, padding: '10px', backgroundColor: '#f8fafc', borderRadius: 8 },
    pnlItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    actionRow: { display: 'flex', justifyContent: 'center', marginTop: 12 },
    actionBadge: { padding: '6px 16px', borderRadius: 6, fontSize: '11px', fontWeight: 'bold', color: 'white' },
    filterChip: { padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border-color)', backgroundColor: 'white', fontSize: '11px', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    filterChipActive: { backgroundColor: 'rgba(37, 99, 235, 0.1)', borderColor: '#2563eb', color: '#2563eb', fontWeight: 'bold' },
    filterChipActiveTrend: { backgroundColor: 'rgba(124, 58, 237, 0.1)', borderColor: '#7c3aed', color: '#7c3aed', fontWeight: 'bold' },
};

export default MobileSwing;
